import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  TextField, 
  Button, 
  FormControlLabel, 
  Checkbox, 
  Typography,

  Paper,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import backgroundImage from '../images/aletheia_login_background.png';
import logoImage from '../logos/aletheia-logo-white.png';
import { useAuth } from '../contexts/AuthContext';
import { AuthError } from '../utils/errorHandler';

// Styled components
const LoginContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundImage: `url(${backgroundImage})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  padding: theme.spacing(2),
}));

const LoginPanel = styled(Paper)(({ theme }) => ({
  backgroundColor: 'rgba(30, 30, 30, 0.95)',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4),
  width: '100%',
  maxWidth: 400,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(3),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  backdropFilter: 'blur(10px)',
}));

const LogoImage = styled('img')({
  width: 80,
  height: 'auto',
  marginBottom: 16,
  objectFit: 'contain',
});

const CompanyName = styled(Typography)(({ theme }) => ({
  fontFamily: '"Outfit", sans-serif',
  fontWeight: 600,
  fontSize: '2rem',
  color: '#FFFFFF',
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(1),
  textAlign: 'center',
}));

const FormSection = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(60, 60, 60, 0.8)',
    borderRadius: theme.spacing(1),
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '& input': {
      color: '#FFFFFF',
      fontFamily: '"Inter", sans-serif',
      '&::placeholder': {
        color: 'rgba(255, 255, 255, 0.6)',
        opacity: 1,
      },
    },
    '& label': {
      color: '#FFFFFF',
      fontFamily: '"Outfit", sans-serif',
      fontWeight: 500,
    },
    '& label.Mui-focused': {
      color: theme.palette.primary.main,
    },
  },
}));

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.7)',
  '&.Mui-checked': {
    color: theme.palette.primary.main,
  },
}));

const CheckboxLabel = styled(Typography)({
  fontFamily: '"Outfit", sans-serif',
  color: '#FFFFFF',
  fontSize: '0.9rem',
});

const LoginButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: '#FFFFFF',
  fontFamily: '"Outfit", sans-serif',
  fontWeight: 600,
  fontSize: '1rem',
  padding: theme.spacing(1.5, 4),
  borderRadius: theme.spacing(1),
  textTransform: 'none',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoggingIn, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [authError, setAuthError] = useState<AuthError | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/home';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location.state]);

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: field === 'rememberMe' ? event.target.checked : event.target.value,
    }));
    // Clear errors when user starts typing
    if (error) setError(null);
    if (authError) setAuthError(null);
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setAuthError(null);
    
    try {
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        // The useEffect will handle the redirect when isAuthenticated changes
        // No need to manually navigate here
      } else {
        setError(result.error || 'Login failed');
        if (result.authError) {
          setAuthError(result.authError);
        }
      }
    } catch (err: any) {
      console.error('Login submission error:', err);
      setError(err.message || 'An unexpected error occurred');
    }
  };

  return (
    <LoginContainer>
      <LoginPanel elevation={24}>
        <LogoImage src={logoImage} alt="Aletheia Logo" />
        <CompanyName variant="h3">ALETHEIA</CompanyName>
        
        <Box 
          component="form" 
          onSubmit={handleSubmit} 
          sx={{ width: '100%' }}
        >
          {error && (
            <Alert 
              severity={authError?.type === 'validation' ? 'warning' : 'error'} 
              sx={{ mb: 2, width: '100%' }}
            >
              {error}
              {authError && authError.type !== 'validation' && (
                <Box sx={{ mt: 1, fontSize: '0.875rem', opacity: 0.8 }}>
                  {authError.type === 'network' && 'This appears to be a network issue. Please check your connection and try again.'}
                  {authError.type === 'server' && 'This appears to be a temporary server issue. Please try again in a few moments.'}
                  {authError.type === 'authentication' && 'Please verify your credentials and try again.'}
                </Box>
              )}
            </Alert>
          )}
          
          <FormSection>
            <StyledTextField
              fullWidth
              label="Login:"
              placeholder="Enter your email..."
              value={formData.email}
              onChange={handleInputChange('email')}
              variant="outlined"
              required
              disabled={isLoggingIn}
            />
            
            <StyledTextField
              fullWidth
              label="Password:"
              placeholder="Enter your password..."
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleInputChange('password')}
              variant="outlined"
              required
              disabled={isLoggingIn}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                      disabled={isLoggingIn}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&:hover': {
                          color: 'rgba(255, 255, 255, 0.9)',
                        },
                      }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <FormControlLabel
              control={
                <StyledCheckbox
                  checked={formData.rememberMe}
                  onChange={handleInputChange('rememberMe')}
                  disabled={isLoggingIn}
                />
              }
              label={<CheckboxLabel>Remember me</CheckboxLabel>}
            />
            
            <LoginButton
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoggingIn}
            >
              {isLoggingIn ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Login'
              )}
            </LoginButton>
          </FormSection>
        </Box>
      </LoginPanel>
    </LoginContainer>
  );
};

export default LoginPage;
